import type { CardDefinition } from './card-definition'
import { validateCatalog } from './validate-catalog'

// Generated at https://claude.ai/share/bc4d9208-a1ac-4028-bc76-310ad4fc4703
export const cardDefinitionCatalog: CardDefinition[] = validateCatalog(
  {
    cardName: 'Fast Food Consumption',
    cardPurpose: 'Captures quantifiable patterns of fast food consumption.',
    cardSet: 'Activity-Based',
    fields: [
      {
        name: 'weeklyVisitFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 21],
        },
        description: 'Number of fast food visits per week',
        purpose: 'Measures frequency of fast food consumption',
      },
      {
        name: 'monthlyUniqueChains',
        legalValues: {
          type: 'integer',
          range: [0, 20],
        },
        description: 'Number of different fast food chains visited monthly',
        purpose: 'Quantifies variety-seeking behavior',
      },
      {
        name: 'averageOrderValue',
        legalValues: {
          type: 'decimal',
        },
        description: 'Average amount spent per fast food order',
        purpose: 'Measures spending level on fast food',
      },
      {
        name: 'deliveryOrderPercentage',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of orders delivered rather than in-store',
        purpose: 'Indicates preference for delivery vs. in-person',
      },
      {
        name: 'limitedTimeOfferPurchases',
        legalValues: {
          type: 'integer',
          range: [0, 30],
        },
        description: 'Number of limited-time offers purchased monthly',
        purpose: 'Measures responsiveness to new products and promotions',
      },
    ],
  },
  {
    cardName: 'Home Cooking',
    cardPurpose: 'Measures engagement with cooking activities at home.',
    cardSet: 'Activity-Based',
    fields: [
      {
        name: 'weeklyMealPreparation',
        legalValues: {
          type: 'integer',
          range: [0, 21],
        },
        description: 'Number of meals prepared at home weekly',
        purpose: 'Quantifies frequency of home cooking',
      },
      {
        name: 'averageRecipeComplexity',
        legalValues: {
          type: 'integer',
          range: [1, 5],
        },
        description: 'Average complexity level of recipes attempted',
        purpose: 'Measures sophistication of cooking activities',
      },
      {
        name: 'monthlyNewRecipes',
        legalValues: {
          type: 'integer',
          range: [0, 50],
        },
        description: 'Number of new recipes tried monthly',
        purpose: 'Indicates openness to culinary experimentation',
      },
      {
        name: 'specialtyIngredientPurchases',
        legalValues: {
          type: 'integer',
          range: [0, 30],
        },
        description: 'Monthly purchases of specialty ingredients',
        purpose: 'Measures interest in gourmet or unique cooking',
      },
      {
        name: 'cookingAppliances',
        legalValues: {
          type: 'integer',
          range: [0, 20],
        },
        description: 'Number of specialty cooking appliances owned',
        purpose: 'Quantifies investment in cooking equipment',
      },
    ],
  },
  {
    cardName: 'Restaurant Dining',
    cardPurpose: 'Captures patterns of restaurant visits and preferences.',
    cardSet: 'Activity-Based',
    fields: [
      {
        name: 'monthlyDiningOutFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 30],
        },
        description: 'Number of restaurant meals per month',
        purpose: 'Measures frequency of dining out',
      },
      {
        name: 'averageCheckAmount',
        legalValues: {
          type: 'decimal',
        },
        description: 'Average amount spent per restaurant visit',
        purpose: 'Indicates price point preferences for dining',
      },
      {
        name: 'cuisineVariety',
        legalValues: {
          type: 'integer',
          range: [0, 20],
        },
        description: 'Number of different cuisine types tried quarterly',
        purpose: 'Measures openness to diverse food experiences',
      },
      {
        name: 'reservationFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 30],
        },
        description: 'Number of reservations made monthly',
        purpose: 'Indicates planning behavior and restaurant tier',
      },
      {
        name: 'foodPhotographed',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of meals photographed',
        purpose: 'Measures social sharing behavior around dining',
      },
    ],
  },
  {
    cardName: 'Coffee Consumption',
    cardPurpose: 'Tracks coffee drinking habits and preferences.',
    cardSet: 'Activity-Based',
    fields: [
      {
        name: 'dailyCupsConsumed',
        legalValues: {
          type: 'integer',
          range: [0, 10],
        },
        description: 'Average number of coffee cups consumed daily',
        purpose: 'Quantifies level of coffee consumption',
      },
      {
        name: 'cafePurchaseFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 30],
        },
        description: 'Number of coffee shop purchases monthly',
        purpose: 'Measures preference for café vs. home brewing',
      },
      {
        name: 'specialtyBeveragePercentage',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of coffee consumed as specialty drinks',
        purpose: 'Indicates interest in coffee variety and complexity',
      },
      {
        name: 'homeBrewingEquipment',
        legalValues: {
          type: 'integer',
          range: [0, 10],
        },
        description: 'Number of coffee brewing tools/appliances owned',
        purpose: 'Measures investment in coffee preparation',
      },
      {
        name: 'beanOriginVariety',
        legalValues: {
          type: 'integer',
          range: [0, 20],
        },
        description: 'Number of different bean origins/varieties tried quarterly',
        purpose: 'Indicates level of coffee connoisseurship',
      },
    ],
  },
  {
    cardName: 'Running Activity',
    cardPurpose: 'Measures engagement with running as an exercise activity.',
    cardSet: 'Activity-Based',
    fields: [
      {
        name: 'weeklyRunningFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 14],
        },
        description: 'Number of running sessions per week',
        purpose: 'Quantifies regularity of running activity',
      },
      {
        name: 'averageRunDistance',
        legalValues: {
          type: 'decimal',
        },
        description: 'Average distance in miles/kilometers per run',
        purpose: 'Measures intensity of running sessions',
      },
      {
        name: 'annualRaceParticipation',
        legalValues: {
          type: 'integer',
          range: [0, 20],
        },
        description: 'Number of organized races joined annually',
        purpose: 'Indicates commitment to running as a sport',
      },
      {
        name: 'runningGearPurchases',
        legalValues: {
          type: 'integer',
          range: [0, 20],
        },
        description: 'Number of running-specific items purchased annually',
        purpose: 'Measures investment in running equipment',
      },
      {
        name: 'runTrackingFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of runs tracked with apps/devices',
        purpose: 'Indicates tech engagement with running activity',
      },
    ],
  },
  {
    cardName: 'Cycling Activity',
    cardPurpose: 'Captures bicycle riding patterns for exercise or transportation.',
    cardSet: 'Activity-Based',
    fields: [
      {
        name: 'weeklyCyclingFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 14],
        },
        description: 'Number of cycling sessions per week',
        purpose: 'Quantifies regularity of cycling activity',
      },
      {
        name: 'averageCyclingDistance',
        legalValues: {
          type: 'decimal',
        },
        description: 'Average distance in miles/kilometers per ride',
        purpose: 'Measures intensity of cycling sessions',
      },
      {
        name: 'cyclingPrimaryPurpose',
        legalValues: {
          type: 'enum',
          options: ['Commuting', 'Exercise', 'Recreation', 'Competition', 'Errands'],
        },
        description: 'Primary purposes for cycling activities',
        purpose: 'Distinguishes between different cycling motivations',
      },
      {
        name: 'cyclingSecondaryPurpose',
        legalValues: {
          type: 'enum',
          options: ['Commuting', 'Exercise', 'Recreation', 'Competition', 'Errands'],
        },
        description: 'Secondary purposes for cycling activities',
        purpose: 'Distinguishes between different cycling motivations',
      },
      {
        name: 'bicycleValue',
        legalValues: {
          type: 'decimal',
        },
        description: 'Value of primary bicycle(s) in use',
        purpose: 'Measures investment level in cycling equipment',
      },
      {
        name: 'cyclistGroupRides',
        legalValues: {
          type: 'integer',
          range: [0, 20],
        },
        description: 'Monthly participation in group cycling activities',
        purpose: 'Indicates social dimension of cycling engagement',
      },
    ],
  },
  {
    cardName: 'Hiking Activity',
    cardPurpose: 'Tracks engagement with hiking and trail activities.',
    cardSet: 'Activity-Based',
    fields: [
      {
        name: 'monthlyHikingFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 30],
        },
        description: 'Number of hiking outings per month',
        purpose: 'Quantifies regularity of hiking activity',
      },
      {
        name: 'averageHikeLength',
        legalValues: {
          type: 'decimal',
        },
        description: 'Average hike distance in miles/kilometers',
        purpose: 'Measures intensity of hiking activity',
      },
      {
        name: 'annualUniqueTrails',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Number of different trails hiked per year',
        purpose: 'Indicates variety-seeking in hiking locations',
      },
      {
        name: 'hikingGearValue',
        legalValues: {
          type: 'decimal',
        },
        description: 'Estimated value of hiking equipment owned',
        purpose: 'Measures investment in hiking activity',
      },
      {
        name: 'overnightHikes',
        legalValues: {
          type: 'integer',
          range: [0, 50],
        },
        description: 'Number of overnight hiking trips annually',
        purpose: 'Indicates commitment level to hiking experiences',
      },
    ],
  },
  {
    cardName: 'Streaming Media Consumption',
    cardPurpose: 'Measures engagement with streaming video services.',
    cardSet: 'Activity-Based',
    fields: [
      {
        name: 'dailyStreamingHours',
        legalValues: {
          type: 'decimal',
          range: [0, 24],
        },
        description: 'Average hours spent streaming video content daily',
        purpose: 'Quantifies time investment in streaming media',
      },
      {
        name: 'streamingServiceCount',
        legalValues: {
          type: 'integer',
          range: [0, 20],
        },
        description: 'Number of streaming services subscribed to',
        purpose: 'Measures breadth of streaming engagement',
      },
      {
        name: 'weeklyBingeWatchingFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 14],
        },
        description: 'Episodes watched in succession weekly',
        purpose: 'Identifies binge-watching behavior patterns',
      },
      {
        name: 'contentGenreVariety',
        legalValues: {
          type: 'integer',
          range: [0, 20],
        },
        description: 'Number of different content genres regularly watched',
        purpose: 'Indicates diversity of entertainment interests',
      },
      {
        name: 'newReleaseViewingPercentage',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of viewing devoted to new releases',
        purpose: 'Measures interest in current vs. catalog content',
      },
    ],
  },
  {
    cardName: 'Online Shopping Behavior',
    cardPurpose: 'Captures patterns of e-commerce activity.',
    cardSet: 'Activity-Based',
    fields: [
      {
        name: 'monthlyOnlineOrders',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Number of online purchases made monthly',
        purpose: 'Quantifies frequency of online shopping',
      },
      {
        name: 'averageOrderValue',
        legalValues: {
          type: 'decimal',
        },
        description: 'Average amount spent per online order',
        purpose: 'Measures spending level in online transactions',
      },
      {
        name: 'productCategoryVariety',
        legalValues: {
          type: 'integer',
          range: [0, 20],
        },
        description: 'Number of different product categories purchased quarterly',
        purpose: 'Indicates breadth of online shopping interests',
      },
      {
        name: 'cartAbandonmentRate',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of shopping carts abandoned',
        purpose: 'Measures purchase decision hesitancy',
      },
      {
        name: 'mobilePurchasePercentage',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of online purchases made via mobile',
        purpose: 'Indicates device preference for shopping',
      },
    ],
  },
  {
    cardName: 'Gaming Engagement',
    cardPurpose: 'Tracks video game playing patterns across platforms.',
    cardSet: 'Activity-Based',
    fields: [
      {
        name: 'weeklyGamingHours',
        legalValues: {
          type: 'decimal',
          range: [0, 100],
        },
        description: 'Hours spent playing video games weekly',
        purpose: 'Quantifies time investment in gaming',
      },
      {
        name: 'gamingPlatformCount',
        legalValues: {
          type: 'integer',
          range: [0, 10],
        },
        description: 'Number of different gaming platforms used',
        purpose: 'Measures breadth of gaming engagement',
      },
      {
        name: 'annualGamePurchases',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Number of games purchased yearly',
        purpose: 'Indicates financial investment in gaming',
      },
      {
        name: 'multiplayerPercentage',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of gaming time spent in multiplayer',
        purpose: 'Measures social dimension of gaming activity',
      },
      {
        name: 'gameGenreVariety',
        legalValues: {
          type: 'integer',
          range: [0, 20],
        },
        description: 'Number of different game genres played',
        purpose: 'Identifies diversity of gaming interests',
      },
    ],
  },
  /* Additional Activity-Based Cards would continue here */

  /* INTEREST DOMAIN CARDS (45) */

  {
    cardName: 'Culinary Interests',
    cardPurpose: 'Measures overall engagement with food as an interest area.',
    cardSet: 'Interest Domain',
    fields: [
      {
        name: 'cookbookCollection',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Number of cookbooks owned',
        purpose: 'Quantifies investment in culinary knowledge',
      },
      {
        name: 'foodMediaConsumptionHours',
        legalValues: {
          type: 'decimal',
          range: [0, 40],
        },
        description: 'Weekly hours spent consuming food-related content',
        purpose: 'Measures engagement with culinary media',
      },
      {
        name: 'internationalCuisineExploration',
        legalValues: {
          type: 'integer',
          range: [0, 50],
        },
        description: 'Number of international cuisines explored',
        purpose: 'Indicates openness to diverse food experiences',
      },
      {
        name: 'foodEventAttendance',
        legalValues: {
          type: 'integer',
          range: [0, 50],
        },
        description: 'Annual attendance at food festivals/events',
        purpose: 'Measures participation in food culture',
      },
      {
        name: 'culinarySkillSelfRating',
        legalValues: {
          type: 'integer',
          range: [1, 10],
        },
        description: 'Self-assessed culinary skill level',
        purpose: 'Indicates confidence and involvement in cooking',
      },
    ],
  },
  {
    cardName: 'Fitness Interests',
    cardPurpose: 'Captures overall engagement with fitness as a lifestyle domain.',
    cardSet: 'Interest Domain',
    fields: [
      {
        name: 'fitnessActivityVariety',
        legalValues: {
          type: 'integer',
          range: [0, 20],
        },
        description: 'Number of different fitness activities engaged in',
        purpose: 'Measures breadth of fitness interests',
      },
      {
        name: 'fitnessContentConsumption',
        legalValues: {
          type: 'decimal',
          range: [0, 40],
        },
        description: 'Weekly hours consuming fitness-related content',
        purpose: 'Indicates engagement with fitness information',
      },
      {
        name: 'fitnessEquipmentInvestment',
        legalValues: {
          type: 'decimal',
        },
        description: 'Annual spending on fitness equipment',
        purpose: 'Quantifies financial commitment to fitness',
      },
      {
        name: 'fitnessGoalCount',
        legalValues: {
          type: 'integer',
          range: [0, 20],
        },
        description: 'Number of specific fitness goals currently pursuing',
        purpose: 'Measures intentionality in fitness activities',
      },
      {
        name: 'fitnessAppUsage',
        legalValues: {
          type: 'integer',
          range: [0, 20],
        },
        description: 'Number of fitness-related apps regularly used',
        purpose: 'Indicates technological integration with fitness',
      },
    ],
  },
  {
    cardName: 'Entertainment Preferences',
    cardPurpose: 'Measures engagement across entertainment media types.',
    cardSet: 'Interest Domain',
    fields: [
      {
        name: 'entertainmentTimePercentageMovies',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of movie entertainment time',
        purpose: 'Identifies primary entertainment preferences',
      },
      {
        name: 'entertainmentTimePercentageTv',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of TV entertainment time',
        purpose: 'Identifies primary entertainment preferences',
      },
      {
        name: 'entertainmentTimePercentageMusic',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of music entertainment time',
        purpose: 'Identifies primary entertainment preferences',
      },
      {
        name: 'entertainmentTimePercentageBooks',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of books entertainment time',
        purpose: 'Identifies primary entertainment preferences',
      },
      {
        name: 'entertainmentTimePercentageGames',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of games entertainment time',
        purpose: 'Identifies primary entertainment preferences',
      },
      {
        name: 'entertainmentSpending',
        legalValues: {
          type: 'decimal',
        },
        description: 'Monthly spending on entertainment',
        purpose: 'Quantifies financial investment in entertainment',
      },
      {
        name: 'contentDiscoveryFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'New entertainment content discovered monthly',
        purpose: 'Measures openness to new entertainment',
      },
      {
        name: 'entertainmentSocialSharing',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Weekly instances of sharing entertainment content',
        purpose: 'Indicates social dimension of entertainment consumption',
      },
      {
        name: 'liveEventAttendance',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Annual attendance at live entertainment events',
        purpose: 'Measures preference for in-person experiences',
      },
    ],
  },
  {
    cardName: 'Shopping Preferences',
    cardPurpose: 'Captures overall shopping approach and preferences.',
    cardSet: 'Interest Domain',
    fields: [
      {
        name: 'shoppingPercentageOnline',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        //   properties: {
        //     online: { type: 'integer', range: [0, 100] },
        //     physical: { type: 'integer', range: [0, 100] },
        //     mobileApp: { type: 'integer', range: [0, 100] },
        //   },
        // },
        description: 'Percentage of online shopping',
        purpose: 'Identifies preferred shopping methods',
      },
      {
        name: 'shoppingPercentagePhysical',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of physical shopping',
        purpose: 'Identifies preferred shopping methods',
      },
      {
        name: 'shoppingPercentageMobileApp',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of mobile app shopping',
        purpose: 'Identifies preferred shopping methods',
      },
      {
        name: 'priceConsciousness',
        legalValues: {
          type: 'integer',
          range: [1, 10],
        },
        description: 'Degree of price sensitivity when shopping',
        purpose: 'Measures importance of price in purchase decisions',
      },
      {
        name: 'brandLoyalty',
        legalValues: {
          type: 'integer',
          range: [1, 10],
        },
        description: 'Tendency to purchase from preferred brands',
        purpose: 'Indicates strength of brand relationships',
      },
      {
        name: 'researchIntensity',
        legalValues: {
          type: 'integer',
          range: [1, 10],
        },
        description: 'Depth of pre-purchase research conducted',
        purpose: 'Measures deliberation in shopping decisions',
      },
      {
        name: 'impulsePercentage',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of purchases made impulsively',
        purpose: 'Identifies tendency toward spontaneous buying',
      },
    ],
  },
  {
    cardName: 'Outdoor Recreation',
    cardPurpose: 'Measures interest and engagement in outdoor activities.',
    cardSet: 'Interest Domain',
    fields: [
      {
        name: 'outdoorActivityVariety',
        legalValues: {
          type: 'integer',
          range: [0, 30],
        },
        description: 'Number of different outdoor activities engaged in',
        purpose: 'Measures breadth of outdoor interests',
      },
      {
        name: 'weeklyOutdoorHours',
        legalValues: {
          type: 'decimal',
          range: [0, 100],
        },
        description: 'Hours spent on outdoor activities weekly',
        purpose: 'Quantifies time investment in outdoor recreation',
      },
      {
        name: 'outdoorGearValue',
        legalValues: {
          type: 'decimal',
        },
        description: 'Estimated value of outdoor equipment owned',
        purpose: 'Measures financial investment in outdoor activities',
      },
      {
        name: 'naturalAreaVisits',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Annual visits to parks, forests, or natural areas',
        purpose: 'Indicates frequency of nature engagement',
      },
      {
        name: 'outdoorSkillLevel',
        legalValues: {
          type: 'integer',
          range: [1, 10],
        },
        description: 'Self-assessed outdoor skill competency',
        purpose: 'Measures expertise in outdoor activities',
      },
    ],
  },
  {
    cardName: 'Technology Engagement',
    cardPurpose: 'Captures interest and interaction with technology.',
    cardSet: 'Interest Domain',
    fields: [
      {
        name: 'deviceOwnership',
        legalValues: {
          type: 'integer',
          range: [0, 50],
        },
        description: 'Number of electronic devices owned',
        purpose: 'Quantifies technology adoption level',
      },
      {
        name: 'techNewsConsumption',
        legalValues: {
          type: 'decimal',
          range: [0, 40],
        },
        description: 'Hours spent consuming technology news weekly',
        purpose: 'Measures engagement with tech developments',
      },
      {
        name: 'deviceUpgradeCycle',
        legalValues: {
          type: 'decimal',
        },
        description: 'Average time in years between device upgrades',
        purpose: 'Indicates pace of technology adoption',
      },
      {
        name: 'techTroubleshooting',
        legalValues: {
          type: 'integer',
          range: [1, 10],
        },
        description: 'Self-assessed technical problem-solving ability',
        purpose: 'Measures technical competence',
      },
      {
        name: 'emergingTechInterest',
        legalValues: {
          type: 'integer',
          range: [1, 10],
        },
        description: 'Level of interest in emerging technologies',
        purpose: 'Identifies early adopter tendencies',
      },
    ],
  },
  {
    cardName: 'Fashion Interests',
    cardPurpose: 'Measures engagement with clothing and fashion.',
    cardSet: 'Interest Domain',
    fields: [
      {
        name: 'fashionContentConsumption',
        legalValues: {
          type: 'decimal',
          range: [0, 40],
        },
        description: 'Hours spent consuming fashion-related content weekly',
        purpose: 'Measures engagement with fashion information',
      },
      {
        name: 'clothingPurchaseFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Number of clothing items purchased monthly',
        purpose: 'Quantifies active participation in fashion consumption',
      },
      {
        name: 'fashionBrandAwareness',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Number of fashion brands recognized and followed',
        purpose: 'Indicates knowledge of fashion landscape',
      },
      {
        name: 'styleExperimentation',
        legalValues: {
          type: 'integer',
          range: [1, 10],
        },
        description: 'Willingness to try new fashion styles',
        purpose: 'Measures openness to fashion trends',
      },
      {
        name: 'fashionSpending',
        legalValues: {
          type: 'decimal',
        },
        description: 'Monthly spending on clothing and accessories',
        purpose: 'Quantifies financial investment in fashion',
      },
    ],
  },
  {
    cardName: 'Home & Garden',
    cardPurpose: 'Captures interest in home improvement and gardening.',
    cardSet: 'Interest Domain',
    fields: [
      {
        name: 'homeProjectFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 50],
        },
        description: 'Number of home improvement projects yearly',
        purpose: 'Quantifies active engagement in home enhancement',
      },
      {
        name: 'gardeningHours',
        legalValues: {
          type: 'decimal',
          range: [0, 100],
        },
        description: 'Hours spent on gardening activities weekly',
        purpose: 'Measures time investment in gardening',
      },
      {
        name: 'homeDecorInterest',
        legalValues: {
          type: 'integer',
          range: [1, 10],
        },
        description: 'Level of interest in home decoration',
        purpose: 'Indicates focus on aesthetic home elements',
      },
      {
        name: 'toolCollection',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Number of DIY tools owned',
        purpose: 'Measures investment in home project capabilities',
      },
      {
        name: 'homeGardenSpending',
        legalValues: {
          type: 'decimal',
        },
        description: 'Annual spending on home and garden improvements',
        purpose: 'Quantifies financial commitment to home enhancement',
      },
    ],
  },
  {
    cardName: 'Cultural Activities',
    cardPurpose: 'Measures engagement with arts and cultural experiences.',
    cardSet: 'Interest Domain',
    fields: [
      {
        name: 'culturalVenueVisits',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Annual visits to museums, galleries, theaters',
        purpose: 'Quantifies participation in cultural institutions',
      },
      {
        name: 'artAppreciation',
        legalValues: {
          type: 'integer',
          range: [1, 10],
        },
        description: 'Self-assessed level of art appreciation',
        purpose: 'Measures engagement with visual arts',
      },
      {
        name: 'culturalContentConsumption',
        legalValues: {
          type: 'decimal',
          range: [0, 40],
        },
        description: 'Hours spent consuming arts/cultural content weekly',
        purpose: 'Indicates engagement with cultural information',
      },
      {
        name: 'creativeExpression',
        legalValues: {
          type: 'integer',
          range: [0, 20],
        },
        description: 'Number of creative activities practiced',
        purpose: 'Measures personal participation in arts',
      },
      {
        name: 'culturalEventAttendance',
        legalValues: {
          type: 'integer',
          range: [0, 50],
        },
        description: 'Annual attendance at cultural events',
        purpose: 'Quantifies active participation in cultural scene',
      },
    ],
  },
  {
    cardName: 'Beverage Preferences',
    cardPurpose: 'Captures beverage consumption patterns and preferences.',
    cardSet: 'Interest Domain',
    fields: [
      {
        name: 'beverageVariety',
        legalValues: {
          type: 'integer',
          range: [0, 20],
        },
        description: 'Number of different beverage types regularly consumed',
        purpose: 'Measures breadth of beverage interests',
      },
      {
        name: 'specialtyBeverageConsumption',
        legalValues: {
          type: 'integer',
          range: [0, 50],
        },
        description: 'Monthly specialty drink consumption',
        purpose: 'Indicates interest in premium beverages',
      },
      {
        name: 'beverageKnowledge',
        legalValues: {
          type: 'integer',
          range: [1, 10],
        },
        description: 'Self-assessed beverage knowledge level',
        purpose: 'Measures expertise in beverage appreciation',
      },
      {
        name: 'homeBeveragePreparation',
        legalValues: {
          type: 'integer',
          range: [0, 50],
        },
        description: 'Monthly instances of home beverage preparation',
        purpose: 'Quantifies engagement with beverage creation',
      },
      {
        name: 'beverageSpending',
        legalValues: {
          type: 'decimal',
        },
        description: 'Monthly spending on beverages',
        purpose: 'Measures financial investment in beverages',
      },
    ],
  },
  /* Additional Interest Domain Cards would continue here */

  /* SPENDING PATTERN CARDS (45) */

  {
    cardName: 'Impulse Purchase Behavior',
    cardPurpose: 'Captures patterns of spontaneous, unplanned buying.',
    cardSet: 'Spending Pattern',
    fields: [
      {
        name: 'impulseFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Number of impulse purchases made monthly',
        purpose: 'Quantifies tendency toward spontaneous buying',
      },
      {
        name: 'averageImpulseValue',
        legalValues: {
          type: 'decimal',
        },
        description: 'Average amount spent on impulse purchases',
        purpose: 'Measures financial impact of impulse decisions',
      },
      {
        name: 'postPurchaseRegretRate',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of impulse purchases later regretted',
        purpose: 'Indicates satisfaction with impulse decisions',
      },
      {
        name: 'triggerContextVariety',
        legalValues: {
          type: 'integer',
          range: [0, 10],
        },
        description: 'Number of different contexts triggering impulse purchases',
        purpose: 'Identifies patterns in impulse buying situations',
      },
      {
        name: 'impulseProductCategories',
        legalValues: {
          type: 'integer',
          range: [0, 20],
        },
        description: 'Number of product categories impulsively purchased',
        purpose: 'Measures breadth of impulse purchasing behavior',
      },
    ],
  },
  {
    cardName: 'Discount Hunting Activity',
    cardPurpose: 'Measures engagement with sales, coupons, and discounts.',
    cardSet: 'Spending Pattern',
    fields: [
      {
        name: 'discountAppUsage',
        legalValues: {
          type: 'integer',
          range: [0, 50],
        },
        description: 'Number of times discount apps checked weekly',
        purpose: 'Quantifies active discount seeking behavior',
      },
      {
        name: 'couponRedemptionRate',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of purchases made with coupons/discounts',
        purpose: 'Measures frequency of discount utilization',
      },
      {
        name: 'priceComparisonFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of purchases preceded by price comparison',
        purpose: 'Indicates deliberate price optimization',
      },
      {
        name: 'saleEventParticipation',
        legalValues: {
          type: 'integer',
          range: [0, 50],
        },
        description: 'Number of sale events shopped annually',
        purpose: 'Measures engagement with promotional events',
      },
      {
        name: 'averageDiscountPercentage',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Average percentage discount secured on purchases',
        purpose: 'Quantifies effectiveness of discount seeking',
      },
    ],
  },
  {
    cardName: 'Luxury Item Investment',
    cardPurpose: 'Captures spending patterns on high-end, premium products.',
    cardSet: 'Spending Pattern',
    fields: [
      {
        name: 'luxuryPurchaseFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 50],
        },
        description: 'Number of luxury items purchased annually',
        purpose: 'Quantifies engagement with luxury market',
      },
      {
        name: 'averageLuxurySpend',
        legalValues: {
          type: 'decimal',
        },
        description: 'Average amount spent per luxury purchase',
        purpose: 'Measures financial commitment to luxury items',
      },
      {
        name: 'luxuryCategoryVariety',
        legalValues: {
          type: 'integer',
          range: [0, 20],
        },
        description: 'Number of different luxury categories purchased',
        purpose: 'Indicates breadth of luxury interests',
      },
      {
        name: 'brandPrestigeImportance',
        legalValues: {
          type: 'integer',
          range: [1, 10],
        },
        description: 'Importance of brand prestige in purchase decisions',
        purpose: 'Measures status motivation in luxury consumption',
      },
      {
        name: 'luxuryResearchIntensity',
        legalValues: {
          type: 'integer',
          range: [1, 10],
        },
        description: 'Depth of research before luxury purchases',
        purpose: 'Indicates deliberation in luxury spending',
      },
    ],
  },
  {
    cardName: 'Subscription Budget Allocation',
    cardPurpose: 'Tracks spending on recurring subscription services.',
    cardSet: 'Spending Pattern',
    fields: [
      {
        name: 'activeSubscriptionCount',
        legalValues: {
          type: 'integer',
          range: [0, 50],
        },
        description: 'Number of active subscription services',
        purpose: 'Quantifies breadth of subscription engagement',
      },
      {
        name: 'monthlySubscriptionSpend',
        legalValues: {
          type: 'decimal',
        },
        description: 'Total monthly spending on subscriptions',
        purpose: 'Measures financial commitment to subscriptions',
      },
      {
        name: 'subscriptionCategoryVariety',
        legalValues: {
          type: 'integer',
          range: [0, 10],
        },
        description: 'Number of different subscription categories',
        purpose: 'Indicates diversity of subscription interests',
      },
      {
        name: 'subscriptionRetentionRate',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of subscriptions maintained >1 year',
        purpose: 'Measures loyalty to subscription services',
      },
      {
        name: 'subscriptionUtilizationRate',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of subscriptions actively used monthly',
        purpose: 'Indicates value derived from subscriptions',
      },
    ],
  },
  {
    cardName: 'Seasonal Spending Patterns',
    cardPurpose: 'Captures variations in spending across seasons and holidays.',
    cardSet: 'Spending Pattern',
    fields: [
      {
        name: 'holidaySpendingMultiplier',
        legalValues: {
          type: 'decimal',
          range: [1, 10],
        },
        description: 'Factor by which spending increases during holidays',
        purpose: 'Quantifies holiday spending intensity',
      },
      {
        name: 'seasonalSaleParticipation',
        legalValues: {
          type: 'integer',
          range: [0, 20],
        },
        description: 'Number of seasonal sales shopped annually',
        purpose: 'Measures engagement with seasonal promotions',
      },
      {
        name: 'giftPurchaseFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Number of gifts purchased annually',
        purpose: 'Indicates gift-giving behavior',
      },
      {
        name: 'earlySeasonalShopping',
        legalValues: {
          type: 'integer',
          range: [0, 12],
        },
        description: 'Average months before holidays/events when shopping begins',
        purpose: 'Measures advance planning for seasonal spending',
      },
      {
        name: 'seasonalCategoryVariety',
        legalValues: {
          type: 'integer',
          range: [0, 20],
        },
        description: 'Number of different product categories with seasonal purchasing patterns',
        purpose: 'Indicates breadth of seasonal spending',
      },
    ],
  },
  {
    cardName: 'Entertainment Budget Usage',
    cardPurpose: 'Tracks spending patterns on entertainment products and services.',
    cardSet: 'Spending Pattern',
    fields: [
      {
        name: 'monthlyEntertainmentSpend',
        legalValues: {
          type: 'decimal',
        },
        description: 'Total monthly spending on entertainment',
        purpose: 'Quantifies financial commitment to entertainment',
      },
      {
        name: 'entertainmentSpendingDigital',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of spend on digital entertainment',
        purpose: 'Identifies entertainment spending priorities',
      },
      {
        name: 'entertainmentSpendingPhysical',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of spend on physical entertainment',
        purpose: 'Identifies entertainment spending priorities',
      },
      {
        name: 'entertainmentSpendingExperiences',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of spend on experiences entertainment',
        purpose: 'Identifies entertainment spending priorities',
      },
      {
        name: 'entertainmentImpulseRate',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of entertainment purchases made impulsively',
        purpose: 'Measures spontaneity in entertainment spending',
      },
      {
        name: 'premiumContentPercentage',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of entertainment budget on premium content',
        purpose: 'Indicates preference for premium experiences',
      },
      {
        name: 'entertainmentBudgetGrowth',
        legalValues: {
          type: 'decimal',
          range: [-100, 100],
        },
        description: 'Percentage change in entertainment spending year-over-year',
        purpose: 'Tracks evolution of entertainment priorities',
      },
    ],
  },
  {
    cardName: 'Household Essentials Spending',
    cardPurpose: 'Captures spending patterns on regular household necessities.',
    cardSet: 'Spending Pattern',
    fields: [
      {
        name: 'monthlyEssentialsSpend',
        legalValues: {
          type: 'decimal',
        },
        description: 'Monthly spending on household essentials',
        purpose: 'Quantifies baseline household spending',
      },
      {
        name: 'bulkPurchasingPercentage',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of essentials bought in bulk',
        purpose: 'Indicates cost optimization strategy',
      },
      {
        name: 'brandTierRatioPremium',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of spends on premium brands',
        purpose: 'Identifies how much of the budget goes to premium brands',
      },
      {
        name: 'brandTierRatioMidrange',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of spends on midrange brands',
        purpose: 'Identifies how much of the budget goes to midrange brands',
      },
      {
        name: 'brandTierRatioBudget',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of spends on budget brands',
        purpose: 'Identifies how much of the budget goes to budget brands',
      },
      {
        name: 'essentialsCategoryVariety',
        legalValues: {
          type: 'integer',
          range: [0, 30],
        },
        description: 'Number of different essential product categories',
        purpose: 'Measures breadth of household needs',
      },
      {
        name: 'essentialsShoppingFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 30],
        },
        description: 'Number of essentials shopping trips monthly',
        purpose: 'Indicates essentials shopping patterns',
      },
    ],
  },
  {
    cardName: 'Personal Care Investment',
    cardPurpose: 'Tracks spending on personal grooming and care products.',
    cardSet: 'Spending Pattern',
    fields: [
      {
        name: 'monthlyPersonalCareSpend',
        legalValues: {
          type: 'decimal',
        },
        description: 'Monthly spending on personal care products/services',
        purpose: 'Quantifies financial investment in personal care',
      },
      {
        name: 'premiumBrandPercentage',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of personal care products from premium brands',
        purpose: 'Indicates quality priorities in personal care',
      },
      {
        name: 'personalCareServiceFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 30],
        },
        description: 'Monthly visits to personal care service providers',
        purpose: 'Measures engagement with professional care services',
      },
      {
        name: 'personalCareProductVariety',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Number of different personal care products used regularly',
        purpose: 'Indicates breadth of personal care routine',
      },
      {
        name: 'specialtyIngredientFocus',
        legalValues: {
          type: 'integer',
          range: [1, 10],
        },
        description: 'Importance of specialty ingredients in product selection',
        purpose: 'Measures sophistication of personal care choices',
      },
    ],
  },
  {
    cardName: 'Technology Upgrade Cycle',
    cardPurpose: 'Captures spending patterns on technology refreshes and upgrades.',
    cardSet: 'Spending Pattern',
    fields: [
      {
        name: 'annualTechSpend',
        legalValues: {
          type: 'decimal',
        },
        description: 'Annual spending on technology purchases',
        purpose: 'Quantifies financial investment in technology',
      },
      {
        name: 'averageDeviceLifespan',
        legalValues: {
          type: 'decimal',
        },
        description: 'Average years between device replacements',
        purpose: 'Measures pace of technology refreshes',
      },
      {
        name: 'earlyAdopterPurchases',
        legalValues: {
          type: 'integer',
          range: [0, 20],
        },
        description: 'Annual technology purchases within 3 months of release',
        purpose: 'Indicates early adopter behavior',
      },
      {
        name: 'techCategoryVariety',
        legalValues: {
          type: 'integer',
          range: [0, 20],
        },
        description: 'Number of different technology categories purchased',
        purpose: 'Measures breadth of technology interests',
      },
      {
        name: 'lastGenerationPercentage',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of tech purchases that are previous generation',
        purpose: 'Indicates value orientation in tech purchases',
      },
    ],
  },
  {
    cardName: 'Gift Purchasing Behavior',
    cardPurpose: 'Tracks spending patterns when buying gifts for others.',
    cardSet: 'Spending Pattern',
    fields: [
      {
        name: 'annualGiftCount',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Number of gifts purchased annually',
        purpose: 'Quantifies gift-giving activity',
      },
      {
        name: 'averageGiftValue',
        legalValues: {
          type: 'decimal',
        },
        description: 'Average amount spent per gift',
        purpose: 'Measures typical gift investment',
      },
      {
        name: 'giftPlanningTimeframe',
        legalValues: {
          type: 'integer',
          range: [0, 365],
        },
        description: 'Average days before occasion when gift is purchased',
        purpose: 'Indicates advance planning for gift purchases',
      },
      {
        name: 'personalizationFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of gifts that are personalized',
        purpose: 'Measures effort invested in gift selection',
      },
      {
        name: 'giftingOccasionVariety',
        legalValues: {
          type: 'integer',
          range: [0, 20],
        },
        description: 'Number of different occasions for which gifts are purchased',
        purpose: 'Indicates breadth of gift-giving behavior',
      },
    ],
  },
  /* Additional Spending Pattern Cards would continue here */

  /* LIFE STAGE & TRANSITION CARDS (45) */

  {
    cardName: 'New Homeowner Behaviors',
    cardPurpose: 'Captures activities and purchases related to new home ownership.',
    cardSet: 'Life Stage & Transition',
    fields: [
      {
        name: 'homeownershipDuration',
        legalValues: {
          type: 'decimal',
        },
        description: 'Years since purchasing first home',
        purpose: 'Identifies recency of homeowner transition',
      },
      {
        name: 'homeImprovementProjects',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Number of home improvement projects completed',
        purpose: 'Quantifies active home enhancement engagement',
      },
      {
        name: 'monthlyHomeGoodsSpend',
        legalValues: {
          type: 'decimal',
        },
        description: 'Monthly spending on home-related purchases',
        purpose: 'Measures financial investment in home setup',
      },
      {
        name: 'homeAdviceConsumption',
        legalValues: {
          type: 'decimal',
          range: [0, 40],
        },
        description: 'Weekly hours consuming home-related content',
        purpose: 'Indicates information-seeking about homeownership',
      },
      {
        name: 'homeFurnishingCompletion',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of home furnishing completed',
        purpose: 'Measures progress in home setup process',
      },
    ],
  },
  {
    cardName: 'Parent of Infant Activities',
    cardPurpose: 'Tracks behaviors and purchases related to caring for babies.',
    cardSet: 'Life Stage & Transition',
    fields: [
      {
        name: 'infantAge',
        legalValues: {
          type: 'decimal',
        },
        description: 'Age of youngest child in years',
        purpose: 'Identifies stage within infant care period',
      },
      {
        name: 'monthlyBabySuppliesSpend',
        legalValues: {
          type: 'decimal',
        },
        description: 'Monthly spending on baby supplies',
        purpose: 'Quantifies financial investment in infant care',
      },
      {
        name: 'parentingContentConsumption',
        legalValues: {
          type: 'decimal',
          range: [0, 40],
        },
        description: 'Weekly hours consuming parenting content',
        purpose: 'Measures information-seeking about childcare',
      },
      {
        name: 'babyProductResearch',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Monthly instances of researching baby products',
        purpose: 'Indicates deliberation in parenting purchases',
      },
      {
        name: 'parentGroupParticipation',
        legalValues: {
          type: 'integer',
          range: [0, 30],
        },
        description: 'Monthly instances of parent community engagement',
        purpose: 'Measures social dimension of new parenthood',
      },
    ],
  },
  {
    cardName: 'College Student Consumption',
    cardPurpose: 'Captures spending and activities typical of higher education students.',
    cardSet: 'Life Stage & Transition',
    fields: [
      {
        name: 'educationStatus',
        legalValues: {
          type: 'enum',
          options: ['Undergraduate', 'Graduate', 'Recent Graduate', 'Not a Student'],
        },
        description: 'Current educational status',
        purpose: 'Identifies educational stage',
      },
      {
        name: 'monthlyEducationSpend',
        legalValues: {
          type: 'decimal',
        },
        description: 'Monthly spending on education-related purchases',
        purpose: 'Quantifies financial investment in education',
      },
      {
        name: 'academicResourceUsage',
        legalValues: {
          type: 'decimal',
          range: [0, 100],
        },
        description: 'Weekly hours using academic resources',
        purpose: 'Measures engagement with educational content',
      },
      {
        name: 'studentDiscountUsage',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Monthly instances of using student discounts',
        purpose: 'Indicates price sensitivity and discount awareness',
      },
      {
        name: 'campusActivityParticipation',
        legalValues: {
          type: 'integer',
          range: [0, 30],
        },
        description: 'Monthly participation in campus activities',
        purpose: 'Measures engagement with student community',
      },
    ],
  },
  {
    cardName: 'Retirement Lifestyle',
    cardPurpose: 'Captures activities and spending patterns of retired individuals.',
    cardSet: 'Life Stage & Transition',
    fields: [
      {
        name: 'retirementDuration',
        legalValues: {
          type: 'decimal',
        },
        description: 'Years since retirement began',
        purpose: 'Identifies stage within retirement transition',
      },
      {
        name: 'leisureActivityVariety',
        legalValues: {
          type: 'integer',
          range: [0, 30],
        },
        description: 'Number of different leisure activities engaged in',
        purpose: 'Measures breadth of retirement interests',
      },
      {
        name: 'travelFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 50],
        },
        description: 'Days spent traveling annually',
        purpose: 'Indicates mobility and travel priorities',
      },
      {
        name: 'volunteerHours',
        legalValues: {
          type: 'decimal',
          range: [0, 40],
        },
        description: 'Weekly hours spent volunteering',
        purpose: 'Measures community engagement in retirement',
      },
      {
        name: 'retirementLocationChanges',
        legalValues: {
          type: 'integer',
          range: [0, 10],
        },
        description: 'Number of residence changes since retirement',
        purpose: 'Identifies geographic flexibility in retirement',
      },
    ],
  },
  {
    cardName: 'Wedding Planning Activity',
    cardPurpose: 'Tracks behaviors related to wedding planning and preparation.',
    cardSet: 'Life Stage & Transition',
    fields: [
      {
        name: 'weddingTimeframe',
        legalValues: {
          type: 'decimal',
        },
        description: 'Years until (positive) or since (negative) wedding date',
        purpose: 'Identifies stage in wedding planning process',
      },
      {
        name: 'weddingBudget',
        legalValues: {
          type: 'decimal',
        },
        description: 'Total planned wedding budget',
        purpose: 'Quantifies financial scale of wedding',
      },
      {
        name: 'vendorResearchFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Monthly instances of researching wedding vendors',
        purpose: 'Measures active engagement in wedding planning',
      },
      {
        name: 'weddingContentConsumption',
        legalValues: {
          type: 'decimal',
          range: [0, 40],
        },
        description: 'Weekly hours consuming wedding-related content',
        purpose: 'Indicates information-seeking about weddings',
      },
      {
        name: 'weddingPurchasesMade',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Percentage of wedding-related purchases completed',
        purpose: 'Measures progress in wedding preparation',
      },
    ],
  },
  {
    cardName: 'Career Advancement Investments',
    cardPurpose: 'Captures activities and spending related to career growth.',
    cardSet: 'Life Stage & Transition',
    fields: [
      {
        name: 'careerTransitionStatus',
        legalValues: {
          type: 'enum',
          options: ['Actively Seeking', 'Recent Change', 'Planning Change', 'Stable Position'],
        },
        description: 'Current career transition status',
        purpose: 'Identifies stage in career development',
      },
      {
        name: 'professionalDevelopmentHours',
        legalValues: {
          type: 'decimal',
          range: [0, 40],
        },
        description: 'Weekly hours spent on professional development',
        purpose: 'Quantifies time investment in career growth',
      },
      {
        name: 'annualSkillTrainingSpend',
        legalValues: {
          type: 'decimal',
        },
        description: 'Annual spending on skills training/education',
        purpose: 'Measures financial investment in career development',
      },
      {
        name: 'professionalNetworkingFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 30],
        },
        description: 'Monthly instances of professional networking',
        purpose: 'Indicates social dimension of career advancement',
      },
      {
        name: 'careerContentConsumption',
        legalValues: {
          type: 'decimal',
          range: [0, 40],
        },
        description: 'Weekly hours consuming career-related content',
        purpose: 'Measures information-seeking about careers',
      },
    ],
  },
  {
    cardName: 'Relocation Preparation',
    cardPurpose: 'Tracks activities related to moving to a new location.',
    cardSet: 'Life Stage & Transition',
    fields: [
      {
        name: 'relocationTimeframe',
        legalValues: {
          type: 'decimal',
        },
        description: 'Years until (positive) or since (negative) planned move',
        purpose: 'Identifies stage in relocation process',
      },
      {
        name: 'destinationResearchFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Monthly instances of researching new location',
        purpose: 'Measures active preparation for relocation',
      },
      {
        name: 'movingServiceResearch',
        legalValues: {
          type: 'integer',
          range: [0, 50],
        },
        description: 'Number of moving services researched',
        purpose: 'Indicates deliberation in relocation planning',
      },
      {
        name: 'relocationBudget',
        legalValues: {
          type: 'decimal',
        },
        description: 'Planned budget for relocation expenses',
        purpose: 'Quantifies financial scale of relocation',
      },
      {
        name: 'housingSearchIntensity',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Weekly instances of searching for new housing',
        purpose: 'Measures urgency of housing search',
      },
    ],
  },
  {
    cardName: 'Health Condition Management',
    cardPurpose: 'Tracks activities related to managing health conditions.',
    cardSet: 'Life Stage & Transition',
    fields: [
      {
        name: 'conditionDuration',
        legalValues: {
          type: 'decimal',
        },
        description: 'Years since health condition diagnosis',
        purpose: 'Identifies stage in health management journey',
      },
      {
        name: 'healthcareVisitFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 100],
        },
        description: 'Annual healthcare provider visits',
        purpose: 'Quantifies engagement with healthcare system',
      },
      {
        name: 'healthManagementSpend',
        legalValues: {
          type: 'decimal',
        },
        description: 'Monthly spending on health management',
        purpose: 'Measures financial investment in health',
      },
      {
        name: 'healthInformationConsumption',
        legalValues: {
          type: 'decimal',
          range: [0, 40],
        },
        description: 'Weekly hours consuming health-related content',
        purpose: 'Indicates information-seeking about health',
      },
      {
        name: 'lifestyleAdaptations',
        legalValues: {
          type: 'integer',
          range: [0, 20],
        },
        description: 'Number of lifestyle changes made for health',
        purpose: 'Measures adaptation to health conditions',
      },
    ],
  },
  {
    cardName: 'Empty Nester Adjustments',
    cardPurpose: 'Captures behaviors after children have left the household.',
    cardSet: 'Life Stage & Transition',
    fields: [
      {
        name: 'emptyNestDuration',
        legalValues: {
          type: 'decimal',
        },
        description: 'Years since last child left home',
        purpose: 'Identifies stage in empty nest transition',
      },
      {
        name: 'homeDownsizingConsideration',
        legalValues: {
          type: 'integer',
          range: [1, 10],
        },
        description: 'Level of interest in downsizing housing',
        purpose: 'Measures housing adjustment intentions',
      },
      {
        name: 'newLeisureActivities',
        legalValues: {
          type: 'integer',
          range: [0, 20],
        },
        description: 'Number of new activities started post-children',
        purpose: 'Indicates lifestyle reimagining',
      },
      {
        name: 'socialEngagementFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 30],
        },
        description: 'Monthly instances of social activities',
        purpose: 'Measures social life adjustments',
      },
      {
        name: 'travelFrequencyChange',
        legalValues: {
          type: 'decimal',
          range: [-100, 100],
        },
        description: 'Percentage change in travel frequency',
        purpose: 'Indicates mobility changes after children leave',
      },
    ],
  },
  {
    cardName: 'Fitness Transformation Journey',
    cardPurpose: 'Tracks behaviors related to significant fitness changes.',
    cardSet: 'Life Stage & Transition',
    fields: [
      {
        name: 'transformationDuration',
        legalValues: {
          type: 'decimal',
        },
        description: 'Years since beginning fitness transformation',
        purpose: 'Identifies stage in fitness journey',
      },
      {
        name: 'weeklyExerciseHours',
        legalValues: {
          type: 'decimal',
          range: [0, 40],
        },
        description: 'Hours spent exercising weekly',
        purpose: 'Quantifies time investment in fitness',
      },
      {
        name: 'fitnessSpendingChange',
        legalValues: {
          type: 'decimal',
          range: [-100, 500],
        },
        description: 'Percentage change in fitness-related spending',
        purpose: 'Measures financial commitment to transformation',
      },
      {
        name: 'nutritionFocusLevel',
        legalValues: {
          type: 'integer',
          range: [1, 10],
        },
        description: 'Level of attention to nutritional choices',
        purpose: 'Indicates dietary aspect of fitness journey',
      },
      {
        name: 'progressTrackingFrequency',
        legalValues: {
          type: 'integer',
          range: [0, 30],
        },
        description: 'Monthly instances of tracking fitness progress',
        purpose: 'Measures goal orientation of fitness activities',
      },
    ],
  },
)
